import { forwardRef, ComponentProps } from "react";

export type Props = Omit<
  ComponentProps<"input">,
  "onChange" | "type" | "checked"
> & {
  /** Field name for identification */
  name?: string;
  /** Whether the checkbox is checked */
  checked?: boolean;
  /** Change handler - receives the new checked state */
  onChange?: (checked: boolean) => void;
  /** Blur handler */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Whether the field has an error (for styling and aria-invalid) */
  error?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** ID of the element that describes this input (typically error message) */
  "aria-describedby"?: string;
  /** ID of the element that labels this input */
  "aria-labelledby"?: string;
};

/**
 * Base checkbox component with no styling.
 * Use this as the foundation for styled checkbox components.
 */
export const CheckboxBase = forwardRef<HTMLInputElement, Props>(
  (
    {
      name,
      checked = false,
      onChange,
      onBlur,
      error,
      disabled,
      className,
      id,
      ...props
    },
    ref
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.checked);
    };

    return (
      <input
        ref={ref}
        type="checkbox"
        name={name}
        id={id ?? name}
        checked={checked}
        onChange={handleChange}
        onBlur={onBlur}
        disabled={disabled}
        aria-invalid={error || undefined}
        className={className}
        data-testid={name ? `checkbox-${name}` : undefined}
        {...props}
      />
    );
  }
);

CheckboxBase.displayName = "CheckboxBase";

